import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  TrendingUp, Users, DollarSign, Calendar, Download, LogOut, 
  Filter, RefreshCw, Loader2, ChevronLeft, ChevronRight, FileText 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CHART_COLORS = ['hsl(208, 84%, 36%)', 'hsl(174, 55%, 38%)'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const getToken = () => localStorage.getItem('admin_token');

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Stats error:', error);
    }
  }, [navigate]);

  const fetchDonations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '10');
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('donor_type', typeFilter);

      const response = await fetch(`${API_URL}/api/admin/donations?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setDonations(data.donations);
        setTotal(data.total);
        setPages(data.pages);
      }
    } catch (error) {
      console.error('Donations error:', error);
    }
  }, [navigate, page, statusFilter, typeFilter]);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchDonations()]);
      setLoading(false);
    };

    loadData();
  }, [fetchStats, fetchDonations, navigate]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('donor_type', typeFilter);

      const response = await fetch(`${API_URL}/api/admin/export?${params}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create and download CSV
        const blob = new Blob([data.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `donations_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success('Export downloaded');
      }
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleDownloadReceipt = async (donationId, receiptNumber) => {
    try {
      const response = await fetch(`${API_URL}/api/donations/${donationId}/download-receipt`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.ok) {
        const data = await response.json();
        
        // Convert base64 to blob and download
        const byteCharacters = atob(data.pdf);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `receipt_${receiptNumber}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success('Receipt downloaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Download failed');
      }
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin');
  };

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchDonations()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      confirmed: 'default',
      pending: 'secondary',
      email_verified: 'outline',
      payment_processing: 'secondary',
      expired: 'destructive',
      failed: 'destructive',
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getDonorName = (donation) => {
    return donation.individual_data?.name || donation.business_data?.business_name || 'Unknown';
  };

  const getDonorEmail = (donation) => {
    return donation.individual_data?.email || donation.business_data?.contact_email || '-';
  };

  // Prepare chart data
  const chartData = stats?.daily_totals?.map(d => ({
    date: d.date.split('-').slice(1).join('/'),
    amount: d.amount,
  })) || [];

  const pieData = stats?.donor_breakdown ? [
    { name: 'Individual', value: stats.donor_breakdown.individual?.total || 0 },
    { name: 'Business', value: stats.donor_breakdown.business?.total || 0 },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="min-h-screen bg-muted">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-40">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/donate" className="flex items-center gap-2">
                <img 
                  src="/logo.png" 
                  alt="If You Teach Me Inc." 
                  className="h-10 w-auto"
                />
              </Link>
              <div>
                <h1 className="font-[Space_Grotesk] font-semibold">Admin Dashboard</h1>
                <p className="text-xs text-muted-foreground">
                  {localStorage.getItem('admin_email')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="kpi-total-amount-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Raised</CardTitle>
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <p className="text-2xl font-bold font-[Roboto_Mono]">
                  {formatCurrency(stats?.total_amount || 0)}
                </p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="kpi-donation-count-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Donations</CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <p className="text-2xl font-bold font-[Roboto_Mono]">
                  {stats?.total_count || 0}
                </p>
              )}
            </CardContent>
          </Card>

          <Card data-testid="kpi-30day-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Last 30 Days</CardTitle>
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-24" />
              ) : (
                <>
                  <p className="text-2xl font-bold font-[Roboto_Mono]">
                    {formatCurrency(stats?.thirty_day_amount || 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {stats?.thirty_day_count || 0} donations
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card data-testid="kpi-average-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg. Donation</CardTitle>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <p className="text-2xl font-bold font-[Roboto_Mono]">
                  {formatCurrency(stats?.average_donation || 0)}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2" data-testid="chart-total-amount-card">
            <CardHeader>
              <CardTitle className="text-base">Donations Over Time (30 days)</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : chartData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(208, 84%, 36%)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="hsl(208, 84%, 36%)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        formatter={(value) => [`$${value}`, 'Amount']}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Area
                        type="monotone"
                        dataKey="amount"
                        stroke="hsl(208, 84%, 36%)"
                        fillOpacity={1}
                        fill="url(#colorAmount)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No donation data yet
                </div>
              )}
            </CardContent>
          </Card>

          <Card data-testid="chart-mix-card">
            <CardHeader>
              <CardTitle className="text-base">Donor Type Mix</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <Skeleton className="h-64 w-full" />
              ) : pieData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `$${value}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No donor data yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Donations Table */}
        <Card data-testid="donations-table-card">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <CardTitle className="text-base">Recent Donations</CardTitle>
              
              <div className="flex flex-wrap items-center gap-3">
                <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                  <TabsList>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
                    <TabsTrigger value="pending">Pending</TabsTrigger>
                  </TabsList>
                </Tabs>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[140px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Donor type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="business">Business</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" onClick={handleExport} data-testid="dashboard-export-csv-button">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <Separator />
          
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Donor</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Opt-In</TableHead>
                    <TableHead>Receipt #</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(9)].map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : donations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No donations found
                      </TableCell>
                    </TableRow>
                  ) : (
                    donations.map((donation) => (
                      <TableRow key={donation._id}>
                        <TableCell className="font-[Roboto_Mono] text-sm">
                          {formatDate(donation.created_at)}
                        </TableCell>
                        <TableCell className="font-medium">{getDonorName(donation)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {getDonorEmail(donation)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {donation.donor_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-[Roboto_Mono] font-semibold">
                          ${donation.amount?.toFixed(2)}
                        </TableCell>
                        <TableCell>{getStatusBadge(donation.status)}</TableCell>
                        <TableCell>
                          <Badge variant={donation.opt_in ? "default" : "secondary"}>
                            {donation.opt_in ? "Yes" : "No"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-[Roboto_Mono] text-sm text-muted-foreground">
                          {donation.receipt_number || '-'}
                        </TableCell>
                        <TableCell>
                          {donation.status === 'confirmed' && donation.receipt_number ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadReceipt(donation._id, donation.receipt_number)}
                              title="Download Receipt"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between p-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {(page - 1) * 10 + 1} - {Math.min(page * 10, total)} of {total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    Page {page} of {pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page === pages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
